// Utilitaires de traitement d'image côté client : compression + recadrage.

/**
 * Compresse une image avant upload.
 * - Redimensionne à maxDim (côté le plus long)
 * - Convertit en JPEG qualité 85 (sauf PNG avec transparence)
 * - Ne fait rien si le fichier est déjà petit ET pas une image
 */
export async function compressImage(file, { maxDim = 1600, quality = 0.85 } = {}) {
  if (!file || !file.type?.startsWith('image/')) return file;
  if (file.size < 200 * 1024 && file.type === 'image/jpeg') return file;

  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });

  let { width, height } = img;
  const ratio = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const outType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await new Promise((res) => canvas.toBlob(res, outType, quality));
  if (!blob) return file;

  const ext = outType === 'image/png' ? 'png' : 'jpg';
  const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + `.${ext}`;
  return new File([blob], name, { type: outType });
}

/**
 * Retourne une image blob recadrée selon les pixels donnés.
 * areaPixels: {x, y, width, height} en pixels (produit par react-easy-crop onCropComplete)
 */
export async function cropImage(imageSrc, areaPixels, { quality = 0.9 } = {}) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = areaPixels.width;
  canvas.height = areaPixels.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    areaPixels.x, areaPixels.y, areaPixels.width, areaPixels.height,
    0, 0, areaPixels.width, areaPixels.height
  );
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
  return blob;
}
